import axios from 'axios';
import ls from 'localstorage-slim';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteProps, useNavigate } from 'react-router-dom';
import { saveUserOffer } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { RootState } from '../../../../redux/types';
import { Offer, Partner, Type } from '../../../../types/instant-lease-api';
import {
  getAppCurrentStatus,
  getText,
} from '../../../../utils/getter';
import Navigation from '../../../../utils/navigation';
import Button from '../../../b2b/components/button/button';
import CarSelection from '../../../b2b/components/car-selection/car-selection';
import { ReactComponent as Plus } from '../../../b2b/components/icons/plus.svg';
import PartnerLogo from '../../../b2b/components/partner-logo/partner-logo';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import ResetPassword from '../../auth-pages/reset-password/reset-password';
import Header from '../../header/header';
import { ReactComponent as CarFront } from '../../icons/car-front.svg';
import CouplePhoto from '../../images/overview-photo.jpg';
import './overview.css';

const ActiveAppsOverview: React.FC<RouteProps> = () => {
  // Store
  const { leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Access Token
  const { emailVerified, authId, email, accessToken } = useSelector(
    (state: RootState) => state.auth,
  );

  // Cache State
  const { hasNewOffer } = useSelector((state: RootState) => state.cache);

  // Dispatch hook
  const dispatch = useDispatch();

  // Navigation Hook
  const navigate = useNavigate();

  // Keep only the first app for each publication/pricing pair.
  const uniqueLeaseApplications = leaseApplications.filter(
    (application, index, allApplications) => {
      const publicationId = application.offer?.publication?.publication_id;
      const pricingId = application.offer?.publication?.pricing_id;

      // Only deduplicate when both IDs exist.
      if (publicationId == null || pricingId == null) {
        return true;
      }

      // findIndex returns the first matching pair, so later duplicates are removed.
      return (
        index ===
        allApplications.findIndex(
          (currentApplication) =>
            currentApplication.offer?.publication?.publication_id ===
              publicationId &&
            currentApplication.offer?.publication?.pricing_id === pricingId,
        )
      );
    },
  );

  // User hasMultipleActiveApps
  const hasMultipleActiveApps = leaseApplications.length > 1;

  // Offer data from localstorage if exist
  const [offer, setOffer] = useState<Offer | null>(ls.get<Offer>('oid'));

  const subheadingMultipleCar = [
    'Bitte wähle ein Wunschfahrzeug aus, da nur ein Fahrzeug pro Antrag möglich ist.',
    'Mit der Auswahl eines Fahrzeugs wird das andere automatisch entfernt.',
  ];

  // Make sure offer always is up to date (inSync)
  useEffect(() => {
    if (ls.get<Offer>('oid') !== null) {
      setOffer(ls.get<Offer>('oid'));
    }
  }, [hasNewOffer]);

  useEffect(() => {
    // The offerhub URL can send offerId/offerRefId as the literal strings
    // "null" or "undefined" when no real value exists. Both are truthy in
    // JS so we must explicitly filter them out.
    // Either offerId or offerRefId will have a real value — never both.
    const effectiveOfferId =
      offer?.offerId &&
      offer.offerId !== 'null' &&
      offer.offerId !== 'undefined'
        ? offer.offerId
        : undefined;
    const effectiveOfferRefId =
      offer?.offerRefId &&
      offer.offerRefId !== 'null' &&
      offer.offerRefId !== 'undefined'
        ? offer.offerRefId
        : undefined;
    const hasValidOffer = effectiveOfferId || effectiveOfferRefId;

    if (offer && email && hasValidOffer) {
      saveUserOffer({
        accessToken: accessToken,
        offerId: effectiveOfferId,
        email: email,
        pricingId: offer?.pricingId,
        publicationId: offer?.publicationId,
        vin: offer?.vin,
        type: offer?.type,
        partner: offer?.partner as any,
        loyaltyId: offer?.loyaltyId,
        loyaltyType: offer?.loyaltyType,
        offerRefId: effectiveOfferRefId,
      })
        .then(() => {
          ls.remove('oid');
        })
        .catch(() => console.log('error'));
    }
  }, [offer, accessToken, email]);

  // Navigation class
  const { openOverview } = new Navigation();

  // Resend verification email
  const resendVerificationEmail = () => {
    if (offer) {
      const { offerId, partner, pricingId, publicationId } = offer;
      axios
        .post('/api/notification-v2/send-registration-email', {
          auth0Id: authId,
          partner: partner || Partner.ALD,
          offerId,
          pricingId,
          publicationId,
        })
        .then(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: false,
              notificationTitle: 'E-Mail wurde gesendet',
              notificationBody:
                'Die Mail ist nicht angekommen? Spam-Ordner prüfen oder erneut senden lassen.',
            }),
          );
        })
        .catch(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid.',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    } else {
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Das tut uns leid.',
          notificationBody: getText('error_unable_to_process_data'),
        }),
      );
    }
  };

  if (!emailVerified) {
    return (
      <>
        <div className='header' data-testid='header'>
          <div role='presentation'>
            <PartnerLogo data-testid='header-logo' className='image' />
          </div>
        </div>
        <div className='auth-container verify-email'>
          <div className='auth-form-container login'>
            <div className='auth-form-content login'>
              <h1 className='title'>
                Bitte verifiziere deine <br />
                E-Mail Adresse
              </h1>
              <p className='dark-grey top-16 content-normal'>
                Wir haben dir eine E-Mail mit einem Bestätigungslink an folgende
                E-Mail Adresse geschickt. Bitte klicke in der Mail den Link an,
                um die Registrierung abzuschließen.
              </p>
              <p className='top-24'>
                <span className='content-bold'>E-Mail-Adresse: </span>
                <span className='content-normal'>{email}</span>
              </p>
              <p className='content-bold top-48'>Noch keine E-Mail erhalten?</p>
              <p className='dark-grey top-24 content-normal'>
                Falls du keine E-Mail bekommen hast, überprüfe bitte deinen
                Spam-Ordner. Du kannst auch{' '}
                <a
                  href='/#'
                  onClick={(e) => {
                    e.preventDefault();
                    resendVerificationEmail();
                  }}
                >
                  hier klicken, um die E-Mail erneut anzufordern.
                </a>
              </p>
            </div>
          </div>
          <div className='auth-image-container login'>
            <div
              className='container-background'
              style={{ backgroundImage: `url(${ResetPassword})` }}
            />
          </div>
        </div>
      </>
    );
  }

  if (hasMultipleActiveApps) {
    // Deduplication is applied only to the multi-app list rendering.
    const sortedApps = _.clone(uniqueLeaseApplications);
    sortedApps.sort((a, b) => {
      return moment(a.offer?.leasing_options?.created).isBefore(
        b.offer?.leasing_options?.created,
      )
        ? 1
        : -1;
    });
    return (
      <>
        <Header showSubHeader={false} />
        <div className='header-main-content'>
          <Layout
            className='overview-multiple-apps'
            heading={getText('car_choice')}
            subHeadingwithArrow={subheadingMultipleCar}
          >
            <div className='car-selection-wrapper'>
              {sortedApps.map((app) => {
                const lastChanged = moment(
                  app.offer?.leasing_options?.created || ' ',
                ).format('DD.MM.YYYY');

                const time = moment(
                  app.offer?.leasing_options?.created || ' ',
                ).format('HH:mm');

                const rate =
                  app.type === Type.B2C
                    ? app.offer?.leasing_options?.totals.rate.gross
                    : app.offer?.leasing_options?.totals.rate.net;

                const appState = getAppCurrentStatus(app);

                return (
                  <div
                    key={app.uuid}
                    role='presentation'
                    data-testid='car-selection-container'
                    className='car-selection-container'
                  >
                    <CarSelection
                      uuid={app.uuid}
                      img={app.offer?.vehicle_data?.vehicleImageUrl}
                      appLeaseDate={lastChanged}
                      time={time}
                      carName={app.offer?.vehicle_data?.full_name}
                      color={
                        app.offer?.vehicle_data?.colors?.paint_naming
                          ?.color_code_name
                      }
                      cushion={
                        app.offer?.vehicle_data?.colors?.cushion_naming
                          ?.equipment_name
                      }
                      rate={rate}
                      status={appState}
                    />
                  </div>
                );
              })}
              <div
                className='add-new-car cursor-pointer'
                role='presentation'
                onClick={() =>
                  window.open(import.meta.env.VITE_MOPO_URL, '_self')
                }
              >
                <div className='add-new-car-content'>
                  <div className='icon-wrapper'>
                    <CarFront className='car-icon' />{' '}
                    <div className='add-car-overlay'>
                      <Plus className='scale-150' />
                      <p className='text-sm font-bold'>
                        Neues Fahrzeug hinzufügen
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Layout>
        </div>
      </>
    );
  }

  if (emailVerified && leaseApplications.length === 1) {
    navigate(openOverview(leaseApplications[0].uuid));
  }

  return (
    <>
      <Header showSubHeader={false}></Header>
      <div className='header-main-content'>
        <Layout
          heading='Willkommen bei Ayvens'
          subHeading='Ihr kompetenter Partner für Geschäfts- und Privatleasing'
        >
          <div className='overview-container mobile-hidden'>
            <img
              src={CouplePhoto}
              alt='photo_of_couples'
              className='couple-photo'
            />
            <div className='text-box'>
              <h2 className='heading'>
                Noch ohne passendes Fahrzeug unterwegs?
              </h2>
              <p className='content-normal'>
                Dann wird es Zeit, deinen Traumwagen zu finden! Begib dich auf
                die spannende Reise durch unsere vielfältige Auswahl an
                Fahrzeugen auf mobility.ayvens.de. Hier warten unterschiedliche
                Modelle und Hersteller darauf, von dir entdeckt zu werden. Dein
                perfektes Fahrerlebnis beginnt mit der richtigen Wahl. Wir
                stehen bereit, um dich auf diesem Weg zu unterstützen. Los
                geht's!
              </p>
              <Button
                theme='primary'
                type='button'
                className='back-next long'
                role='presentation'
                onClick={() =>
                  window.open(import.meta.env.VITE_MOPO_URL, '_self')
                }
              >
                weiter zu mobility.ayvens.de
              </Button>
            </div>
          </div>
          <div className='overview-container mobile-only'>
            <ButtonGroup
              className='back-next small center'
              type='default'
              style={{ padding: 0 }}
              buttonOneProps={{
                type: 'button',
                children: 'Privatkunden',
                className: 'button-text',
                onClick: () =>
                  window.open(import.meta.env.VITE_MOPO_URL, '_self'),
                style: { marginTop: 0, height: '48px' },
              }}
            />
            <ButtonGroup
              className='back-next small center'
              type='default'
              style={{ padding: 0 }}
              buttonOneProps={{
                type: 'button',
                children: 'Geschäftskunden',
                className: 'button-text',
                onClick: () =>
                  window.open(import.meta.env.VITE_MOPO_URL, '_self'),
                style: { marginTop: 0, height: '48px' },
              }}
            />
          </div>
        </Layout>
      </div>
    </>
  );
};

export default ActiveAppsOverview;
